import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Cloud, Download, Loader2, Printer, RefreshCw, Scale, Wallet } from 'lucide-react'
import clsx from 'clsx'

import { api, mensajeDeError } from '@/lib/api'
import { tamanoArchivo } from '@/lib/formato'
import { avisar, confirmar } from '@/lib/confirmar'
import { useSesion } from '@/lib/sesion'
import { Pantalla } from '@/componentes/ui/Pantalla'
import { LookupSelect, type ItemLookup } from '@/componentes/ui/LookupSelect'
import { AsistenteRegistro } from './AsistenteRegistro'
import type { LecturaBalanza } from '@/features/pos/tipos'

// Los equipos de ESTA máquina: la balanza, la impresora y el cajón.
//
// Existían los dos dispositivos, sus opciones documentadas una por una y las
// cuatro implementaciones, y NINGUNA pantalla los configuraba: la única forma
// era editar el `appsettings.json` con el Notepad al lado del ejecutable y
// reiniciar el programa. Es el mismo error que la dirección de la nube y que
// la tecla rápida del medio de pago —el mecanismo entero sin la pantalla que
// lo usa— y encima reiniciar, en el mostrador, significa cerrar el sistema con
// gente esperando.
//
// Tres decisiones de la pantalla:
//
//   - Está SOLO en el ejecutable. Estos valores son de la MÁQUINA y no del
//     local: dos cajas del mismo local pueden tener impresoras distintas, así
//     que no bajan de la nube. En un navegador no hay puerto serie que
//     configurar.
//   - Cada equipo se PRUEBA desde acá. Poner un COM y guardar sin saber si
//     salió papel deja a la persona en el mismo lugar que antes: lo iba a
//     descubrir en la primera venta, con un cliente adelante.
//   - Lo de todos los días arriba y el resto detrás de «Mostrar lo avanzado».
//     Lo que se cambia es el modo y el puerto; la paridad, los bits y el
//     patrón de la trama se tocan una vez en la vida y llenar la pantalla con
//     ocho campos para elegir una balanza es lo que hace que nadie la abra.
export function EstaCaja() {
  const { puede } = useSesion()
  const cliente = useQueryClient()

  // Mudar de local o de caja ocupa la pantalla entera: es un flujo de tres
  // pasos y dejarlo en un hueco entre la balanza y la impresora hace dudar de
  // si lo que se está mirando sigue siendo la pantalla de antes.
  const [mudando, setMudando] = useState(false)

  const consulta = useQuery({
    queryKey: ['equipo'],
    queryFn: () => api.get<Equipo>('/equipo'),
    retry: false,
  })

  if (!puede('sistema.actualizar')) return <Pantalla.SinPermiso />
  if (consulta.isPending) return <Pantalla.Cargando />

  if (consulta.isError || !consulta.data) {
    return (
      <div className="p-4">
        <p className="carta p-4 text-[13.5px] text-rojo">
          {mensajeDeError(consulta.error, 'No pudimos leer la configuración de esta caja.')}
        </p>
      </div>
    )
  }

  const refrescar = () => void cliente.invalidateQueries({ queryKey: ['equipo'] })

  if (mudando) {
    return (
      <AsistenteRegistro
        mudando
        onCancelar={() => setMudando(false)}
        onListo={() => {
          // Se RECARGA en lugar de invalidar consultas: cambió el local, así
          // que la sesión, el catálogo, el menú y los permisos son de otro
          // lado. Refrescar de a pedazos dejaría la pantalla mezclando dos
          // locales, que es peor que el segundo que cuesta recargar.
          window.location.reload()
        }}
      />
    )
  }

  return (
    <div className="mx-auto max-w-[760px] space-y-4 p-4">
      <div>
        <h1 className="text-[20px] font-bold tracking-[-0.02em] text-tinta">Esta caja</h1>

        <p className="mt-1 text-[13.5px] leading-relaxed text-tinta-tenue">
          La balanza y la impresora de <strong>esta computadora</strong>. No se comparten con las
          otras cajas del local, y lo que se guarda se aplica en el momento: no hay que cerrar el
          programa.
        </p>
      </div>

      <FormBalanza inicial={consulta.data.balanza} estado={consulta.data.estado} onGuardado={refrescar} />
      <FormImpresora inicial={consulta.data.impresora} estado={consulta.data.estado} onGuardado={refrescar} />
      <PantallaDelSistema />
      <ElLocalYLaNube onMudar={() => setMudando(true)} />
    </div>
  )
}

// ── El local, la caja y la nube ─────────────────────────────────────────────

// A qué local pertenece esta máquina y cómo cambiarlo.
//
// Desde el `.exe` no había forma: los tres endpoints del registro contestaban
// un 409 seco —«esta caja ya está registrada»— así que corregir la dirección
// de la nube o el punto de venta que se eligió mal significaba borrar la base
// local, perdiendo lo que no había subido. Y hace falta poder cambiarlo: la
// nube se muda de dominio, alguien se equivocó en el paso 3, el local cambia
// de proveedor.
//
// El freno correcto no es «ya está registrada» sino «hay cosas sin subir», y
// eso lo decide el servidor: se muestra acá para que la persona lo vea ANTES
// de entrar al asistente, en lugar de descubrirlo en el último paso.
function ElLocalYLaNube({ onMudar }: { onMudar: () => void }) {
  const consulta = useQuery({
    queryKey: ['registro', 'esta-caja'],
    queryFn: () => api.get<Registro>('/registro'),
    retry: false,
  })

  const estado = useQuery({
    queryKey: ['estado', 'pendientes'],
    queryFn: () => api.get<{ pendientes: number; descartados: number }>('/estado'),
    refetchInterval: 5000,
    retry: false,
  })

  const d = consulta.data
  const sinSubir = (estado.data?.pendientes ?? 0) + (estado.data?.descartados ?? 0)

  return (
    <section className="carta p-4">
      <Encabezado
        icono={<Cloud className="h-4 w-4 text-verde-oscuro" />}
        titulo="El local y la nube"
        estado={sinSubir > 0 ? `${sinSubir} sin subir` : 'Todo subido'}
        bien={sinSubir === 0}
      />

      <dl className="mt-3 grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-[auto_1fr]">
        <dt className="text-tinta-tenue">Local</dt>
        <dd className="text-tinta-suave">{d?.local ?? '—'}</dd>

        <dt className="text-tinta-tenue">Esta caja es</dt>
        <dd className="text-tinta-suave">
          {d?.puntoDeVenta ?? '—'}
          {d?.prefijo ? <span className="num text-tinta-tenue"> · {d.prefijo}</span> : null}
        </dd>

        <dt className="text-tinta-tenue">Dirección de la nube</dt>
        <dd className="break-all text-tinta-suave">{d?.nubeUrl || '—'}</dd>
      </dl>

      {sinSubir > 0 && (
        <p className="mt-3 rounded-lg bg-ambar/10 px-3 py-2.5 text-[13px] leading-relaxed text-ambar">
          Quedan <strong className="num">{sinSubir}</strong> operaciones sin subir. No se puede
          cambiar de local ni de caja hasta que suban: pertenecen a esta nube y a este talonario,
          así que cambiar ahora las dejaría sin llegar.
        </p>
      )}

      <p className="mt-3 text-[12.5px] leading-relaxed text-tinta-tenue">
        Se cambia cuando el sistema se mudó de dirección, o cuando esta computadora quedó atada a
        la caja equivocada. Las ventas que ya subieron no se tocan.
      </p>

      <button
        type="button"
        onClick={onMudar}
        disabled={sinSubir > 0}
        className="btn-secundario mt-3"
      >
        Cambiar el local o la caja
      </button>
    </section>
  )
}

// ── La pantalla del sistema (el OTA) ────────────────────────────────────────

// El botón que el código prometía en TRES comentarios y no existía.
//
// `GestorUi` dice «se activa en el próximo arranque o cuando el cajero lo
// pida», `ActualizadorService` repite «eso lo decide el cajero con el botón»,
// y los dos endpoints estaban escritos y andando: lo que faltaba era la
// pantalla que los llame. Es la misma familia que la tecla rápida del medio de
// pago y que la autorización con PIN.
//
// Sin esto, publicar una versión nueva llegaba al mostrador recién al
// reiniciar el programa —que en una caja que se apaga todas las noches es «al
// otro día»—, y el que acaba de publicar no tenía forma de verla.
function PantallaDelSistema() {
  const [aplicando, setAplicando] = useState(false)

  const consulta = useQuery({
    queryKey: ['actualizaciones'],
    queryFn: () => api.get<Actualizacion>('/actualizaciones'),

    // No se pregunta sola al entrar a la pantalla: consultar SALE A INTERNET y
    // esta pantalla se abre para configurar la impresora. Lo dispara el botón.
    enabled: false,
    retry: false,
  })

  const aplicar = async () => {
    const v = consulta.data?.version

    // Se avisa que la pantalla se REINICIA, porque es lo que pasa: activar
    // rota las carpetas del bundle y hay que recargar para que cargue el
    // nuevo. Con una venta a medias eso perdería el ticket, así que la
    // decisión es de la persona y no del sistema.
    const sigue = await confirmar({
      titulo: v ? `Instalar la pantalla ${v}` : 'Instalar la pantalla nueva',
      detalle:
        'La pantalla se va a reiniciar, así que hacelo con el ticket vacío. La venta y el turno '
        + 'no se tocan: lo que cambia es la pantalla, no los datos.',
      confirmar: 'Instalar y reiniciar la pantalla',
    })

    if (!sigue) return

    setAplicando(true)

    try {
      await api.post('/actualizaciones/aplicar', {})

      // Se recarga en el acto, y no es una comodidad: el SPA le confirma al
      // host que montó bien, y si esa confirmación no llega en sesenta
      // segundos la caja vuelve sola a la versión anterior. Sin la recarga,
      // instalar terminaría en un rollback.
      window.location.reload()
    } catch (e) {
      setAplicando(false)

      void avisar({
        titulo: 'No se pudo instalar',
        detalle: mensajeDeError(e, 'No se pudo instalar la pantalla nueva.'),
      })
    }
  }

  const d = consulta.data

  return (
    <section className="carta p-4">
      <Encabezado
        icono={<RefreshCw className="h-4 w-4 text-verde-oscuro" />}
        titulo="Pantalla del sistema"
        estado={d ? (d.hayNueva ? 'Hay una versión nueva' : 'Al día') : ''}
        bien={d ? !d.hayNueva : null}
      />

      <p className="mt-2 text-[13px] leading-relaxed text-tinta-tenue">
        La pantalla se actualiza sola: la caja la baja cuando hay una nueva y la instala en el
        próximo arranque. Desde acá se puede instalar antes, sin esperar a mañana.
      </p>

      {d && (
        <dl className="mt-3 grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-[auto_1fr]">
          <dt className="text-tinta-tenue">La que está puesta</dt>
          <dd className="num text-tinta-suave">{d.versionActual ?? '—'}</dd>

          {d.hayNueva && (
            <>
              <dt className="text-tinta-tenue">La que hay disponible</dt>
              <dd className="num text-tinta-suave">{d.version ?? '—'}</dd>
            </>
          )}
        </dl>
      )}

      {d?.requiereActualizarPrograma && (
        <p className="mt-3 rounded-lg bg-ambar/10 px-3 py-2.5 text-[13px] leading-relaxed text-ambar">
          La pantalla nueva necesita una versión más nueva del <strong>programa</strong> de la caja.
          Hay que reinstalar el ejecutable; la pantalla sola no alcanza.
        </p>
      )}

      {d?.programa && <ProgramaDeLaMaquina programa={d.programa} />}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void consulta.refetch()}
          disabled={consulta.isFetching}
          className="btn-secundario"
        >
          {consulta.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Buscar una versión nueva
        </button>

        {d?.hayNueva && !d.requiereActualizarPrograma && (
          <button
            type="button"
            onClick={() => void aplicar()}
            disabled={aplicando}
            className="btn-primario"
          >
            {aplicando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Instalar ahora
          </button>
        )}

        {consulta.isError && (
          <span className="text-[13px] text-ambar">
            {mensajeDeError(consulta.error, 'No se pudo consultar la nube.')}
          </span>
        )}
      </div>
    </section>
  )
}

// ── Balanza ─────────────────────────────────────────────────────────────────

const MODOS_BALANZA: ItemLookup[] = [
  { id: 1, code: 'ninguna', nombre: 'No tiene balanza', detalle: 'El peso se escribe a mano' },
  { id: 2, code: 'serie', nombre: 'Balanza por cable (puerto serie)', detalle: 'El peso aparece solo' },
  { id: 3, code: 'simulada', nombre: 'Simulada', detalle: 'Pesos inventados, para probar el sistema' },
]

function FormBalanza({
  inicial,
  estado,
  onGuardado,
}: {
  inicial: OpcionesBalanza
  estado: EstadoEquipo
  onGuardado: () => void
}) {
  const [o, setO] = useState(inicial)
  const [avanzado, setAvanzado] = useState(false)
  const [guardado, setGuardado] = useState(false)

  const campo = <K extends keyof OpcionesBalanza>(k: K, v: OpcionesBalanza[K]) => {
    setO((p) => ({ ...p, [k]: v }))
    setGuardado(false)
  }

  const guardar = useMutation({
    mutationFn: () => api.put('/equipo/balanza', o),
    onSuccess: () => {
      setGuardado(true)
      onGuardado()
    },
    onError: (e) =>
      void avisar({
        titulo: 'No se pudo guardar la balanza',
        detalle: mensajeDeError(e, 'No se pudo guardar la configuración de la balanza.'),
      }),
  })

  const esSerie = o.modo === 'serie'
  const dispositivos = useDispositivos()

  return (
    <section className="carta p-4">
      <Encabezado
        icono={<Scale className="h-4 w-4 text-verde-oscuro" />}
        titulo="Balanza"
        estado={
          o.modo === 'ninguna'
            ? 'No hay balanza configurada'
            : estado.balanzaConectada
              ? 'Conectada'
              : 'Configurada, sin responder'
        }
        bien={o.modo === 'ninguna' ? null : estado.balanzaConectada}
      />

      <div className="mt-3 space-y-3">
        <LookupSelect
          etiqueta="Qué balanza tiene esta caja"
          items={MODOS_BALANZA}
          valor={MODOS_BALANZA.find((m) => m.code === o.modo)?.id ?? null}
          onCambio={(_, item) => campo('modo', item?.code ?? 'ninguna')}
        />

        {esSerie && (
          <SelectorDeAparato
            etiqueta="Puerto"
            ayuda="En Windows es COM1, COM2, COM3… En Linux, /dev/ttyUSB0."
            vacio="No hay ningún puerto serie disponible. Revisá que la balanza esté enchufada y prendida."
            valor={o.puerto}
            onCambio={(v) => campo('puerto', v)}
            opciones={dispositivos.data?.puertos ?? []}
            cargando={dispositivos.isFetching}
            onBuscar={() => void dispositivos.refetch()}
          />
        )}

        {esSerie && (
          <>
            <BotonAvanzado abierto={avanzado} onCambio={setAvanzado} />

            {avanzado && (
              <div className="grid gap-3 rounded-lg bg-crema-alt p-3 sm:grid-cols-2">
                <Numero etiqueta="Baudios" valor={o.baudios} onCambio={(v) => campo('baudios', v)} />
                <Numero
                  etiqueta="Bits de datos"
                  valor={o.bitsDeDatos}
                  onCambio={(v) => campo('bitsDeDatos', v)}
                />

                <Texto etiqueta="Paridad" valor={o.paridad} onCambio={(v) => campo('paridad', v)} />
                <Texto
                  etiqueta="Bits de parada"
                  valor={o.bitsDeParada}
                  onCambio={(v) => campo('bitsDeParada', v)}
                />

                <label className="block sm:col-span-2">
                  <span className="etiqueta">Patrón de la trama</span>

                  <input
                    className="campo font-mono text-[13px]"
                    value={o.patronTrama}
                    onChange={(e) => campo('patronTrama', e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />

                  <span className="mt-1 block text-[12.5px] leading-relaxed text-tinta-tenue">
                    Cada marca de balanza manda el peso de una forma distinta. Necesita un grupo
                    llamado <code>kilos</code> o <code>gramos</code>. Si la tuya anda con el valor
                    que viene, no lo toques.
                  </span>
                </label>

                <Texto
                  etiqueta="Marca de lectura estable"
                  valor={o.marcaEstable}
                  onCambio={(v) => campo('marcaEstable', v)}
                />

                <Numero
                  etiqueta="Vencimiento de la lectura (ms)"
                  valor={o.vencimientoMs}
                  onCambio={(v) => campo('vencimientoMs', v)}
                />
              </div>
            )}
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => guardar.mutate()}
            disabled={guardar.isPending}
            className="btn-primario"
          >
            {guardar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Guardar
          </button>

          {o.modo !== 'ninguna' && <PesoEnVivo />}

          {guardado && <span className="text-[13px] text-verde-oscuro">Guardado.</span>}
        </div>
      </div>
    </section>
  )
}

// La prueba de la balanza no es un botón: es MIRAR el peso. Se pide cada medio
// segundo mientras la sección está abierta, que es lo mismo que hace el POS,
// así la persona apoya algo y ve si el número se mueve.
function PesoEnVivo() {
  const { data } = useQuery({
    queryKey: ['balanza', 'peso'],
    queryFn: () => api.get<LecturaBalanza>('/balanza/peso'),
    refetchInterval: 500,
    retry: false,
  })

  const texto = !data
    ? 'Leyendo…'
    : !data.conectada
      ? 'No responde'
      : !data.hayLectura
        ? 'Conectada, sin peso'
        : `${((data.gramos ?? 0) / 1000).toFixed(3)} kg${data.estable ? '' : ' (moviéndose)'}`

  return (
    <span className="num rounded-lg bg-crema-alt px-2.5 py-1.5 text-[13px] text-tinta-suave">
      {texto}
    </span>
  )
}

// ── Elegir un aparato en lugar de adivinarlo ────────────────────────────────

interface Dispositivo {
  valor: string
  nombre: string
  detalle?: string
}

interface DispositivosDeLaMaquina {
  impresoras: Dispositivo[]
  puertos: Dispositivo[]
  esWindows: boolean
}

/**
 * Qué impresoras y qué puertos tiene ESTA computadora.
 *
 * Se consulta una vez y se comparte entre la balanza y la impresora: son la
 * misma pregunta al mismo host. No se cachea para siempre a propósito —un COM
 * aparece cuando alguien enchufa el aparato— pero tampoco se pregunta en cada
 * tecla: el botón «Buscar de nuevo» es el que vuelve a mirar.
 */
function useDispositivos() {
  return useQuery({
    queryKey: ['equipo', 'dispositivos'],
    queryFn: () => api.get<DispositivosDeLaMaquina>('/equipo/dispositivos'),
    staleTime: 30_000,
  })
}

/**
 * El combo de «a qué aparato le hablo».
 *
 * Muestra lo que la máquina TIENE y además deja escribir uno a mano, y las dos
 * mitades son necesarias: sin la lista, el verdulero tiene que averiguar un
 * puerto o el nombre exacto de una impresora —que es justo el dato que la
 * máquina ya sabe—; y sin la escritura a mano, una lista que se quedó corta
 * deja la caja sin poder configurarse, porque un COM aparece recién cuando el
 * aparato está enchufado y prendido y una impresora de red puede no estar
 * instalada todavía.
 *
 * El valor que ya está guardado SIEMPRE figura como opción, aunque no se haya
 * detectado: si no, abrir la pantalla mostraría el campo vacío y guardar sin
 * mirar borraría una configuración que andaba.
 */
function SelectorDeAparato({
  etiqueta,
  ayuda,
  valor,
  onCambio,
  opciones,
  cargando,
  onBuscar,
  vacio,
}: {
  etiqueta: string
  ayuda: string
  valor: string
  onCambio: (valor: string) => void
  opciones: Dispositivo[]
  cargando: boolean
  onBuscar: () => void
  vacio: string
}) {
  const items: ItemLookup[] = opciones.map((d, i) => ({
    id: i + 1,
    code: d.valor,
    nombre: d.nombre,
    detalle: d.detalle,
  }))

  const guardado = valor.trim()

  if (guardado && !opciones.some((d) => d.valor === guardado)) {
    items.push({
      id: items.length + 1,
      code: guardado,
      nombre: guardado,
      detalle: 'Lo que está configurado, y ahora no se detecta',
    })
  }

  return (
    <div>
      <LookupSelect
        etiqueta={etiqueta}
        items={items}
        valor={items.find((x) => x.code === guardado)?.id ?? null}
        onCambio={(_, item) => onCambio(item?.code ?? '')}
        onCrear={(texto) => onCambio(texto.trim())}
        textoCrear="Usar"
        placeholder={cargando ? 'Buscando…' : 'Elegí o escribilo'}
      />

      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="text-[12.5px] text-tinta-tenue">
          {opciones.length === 0 && !cargando ? vacio : ayuda}
        </span>

        <button
          type="button"
          onClick={onBuscar}
          className="text-[12.5px] font-medium text-verde hover:underline"
        >
          Buscar de nuevo
        </button>
      </div>
    </div>
  )
}

// ── Impresora ───────────────────────────────────────────────────────────────

// El orden no es alfabético: arriba va lo que elige la mayoría.
//
// «Impresora de Windows» es el primero porque casi todas las térmicas que se
// venden hoy son USB, y una térmica USB en Windows NO es un puerto COM: es una
// impresora del sistema, con su driver y su nombre. Mientras el único modo real
// fue el serie, al verdulero se le pedía un dato que en su máquina muchas veces
// ni existe.
const MODOS_IMPRESORA: ItemLookup[] = [
  {
    id: 1,
    code: 'windows',
    nombre: 'Térmica instalada en Windows',
    detalle: 'La comandera de tickets. Corta el papel y abre el cajón',
  },
  {
    id: 5,
    code: 'documento',
    nombre: 'Impresora común de Windows',
    detalle: 'Tinta, láser o «Print to PDF». Sale como hoja, sin cajón',
  },
  {
    id: 2,
    code: 'escpos',
    nombre: 'Térmica por cable (ESC/POS)',
    detalle: 'Por puerto serie: COM1, o el COM virtual de algunas USB',
  },
  {
    id: 3,
    code: 'simulada',
    nombre: 'Guardar el ticket en un archivo',
    detalle: 'Para probar, y de respaldo si la térmica se queda sin papel',
  },
  { id: 4, code: 'ninguna', nombre: 'No imprime', detalle: 'Sin ticket ni cajón' },
]

function FormImpresora({
  inicial,
  estado,
  onGuardado,
}: {
  inicial: OpcionesImpresora
  estado: EstadoEquipo
  onGuardado: () => void
}) {
  const [o, setO] = useState(inicial)
  const [avanzado, setAvanzado] = useState(false)
  const [guardado, setGuardado] = useState(false)

  const { puede } = useSesion()

  const campo = <K extends keyof OpcionesImpresora>(k: K, v: OpcionesImpresora[K]) => {
    setO((p) => ({ ...p, [k]: v }))
    setGuardado(false)
  }

  // Tres preguntas distintas, y confundirlas es lo que escondía el campo
  // equivocado: `porSerie` es «hay un puerto y baudios que configurar»,
  // `porWindows` es «hay que elegir una impresora de una lista», y
  // `haceTicket` es «esto habla ESC/POS», que vale para el serie Y para la
  // térmica de Windows —los bytes son los mismos, cambia por dónde salen— pero
  // NO para el modo documento, donde el corte y el cajón no existen. El ancho
  // sí vale para los cuatro: es con lo que el armador maqueta el texto.
  const porSerie = o.modo === 'escpos'
  const porDocumento = o.modo === 'documento'
  const porWindows = o.modo === 'windows' || porDocumento
  const haceTicket = porSerie || (porWindows && !porDocumento)

  const guardar = useMutation({
    mutationFn: () => api.put('/equipo/impresora', o),
    onSuccess: () => {
      setGuardado(true)
      onGuardado()
    },
    onError: (e) =>
      void avisar({
        titulo: 'No se pudo guardar la impresora',
        detalle: mensajeDeError(e, 'No se pudo guardar la configuración de la impresora.'),
      }),
  })

  const probar = useMutation({
    mutationFn: () =>
      api.post<{ texto: string; impreso: boolean; motivo: string | null }>(
        '/equipo/impresora/probar',
        {},
      ),

    // Se avisa siempre, salga o no: «no pasó nada» después de apretar un botón
    // de prueba es indistinguible de un botón que no anda. Y cuando no sale,
    // lo que se muestra es el MOTIVO que manda el servidor: el texto de acá es
    // el último recurso, porque «revisá el modo, el puerto y que esté
    // encendida» sirve para todo y no ayuda con nada.
    onSuccess: (r) =>
      void avisar({
        titulo: r.impreso ? 'Salió la prueba' : 'No se pudo imprimir',
        detalle: r.impreso
          ? porDocumento
            ? 'Sale como una hoja de texto, así que el ancho no se nota en el papel: lo que se '
              + 'verifica acá es que la impresora responda y que los acentos y la ñ salgan bien.'
            : 'Mirá el papel: si la regla de números no llega al borde derecho, el ancho no es ese.'
          : (r.motivo
            ?? 'La impresora no aceptó el trabajo. Revisá el modo, el puerto y que esté encendida.'),
      }),

    onError: (e) =>
      void avisar({
        titulo: 'No se pudo imprimir',
        detalle: mensajeDeError(e, 'No se pudo imprimir la prueba.'),
      }),
  })

  const cajon = useMutation({
    mutationFn: () => api.post<{ abierto: boolean; motivo: string | null }>('/impresora/cajon', {}),
    onSuccess: (r) => {
      if (!r.abierto) {
        void avisar({
          titulo: 'El cajón no abrió',
          detalle:
            r.motivo
            ?? 'El cajón se abre por la impresora, con el cable RJ11. Revisá que esté enchufado ahí '
            + 'y probá con el otro comando: hay modelos que usan el pin 2.',
        })
      }
    },
    onError: (e) =>
      void avisar({
        titulo: 'El cajón no abrió',
        detalle: mensajeDeError(e, 'No se pudo abrir el cajón.'),
      }),
  })

  const dispositivos = useDispositivos()

  // Cambiar de modo LIMPIA el aparato cuando cambia de qué se trata.
  //
  // El mismo campo guarda un puerto («COM1») o el nombre de una impresora
  // («Ticketera 80mm»), y pasar de un modo al otro dejaba el valor anterior
  // ahí: elegir «Impresora de Windows» mostraba «COM1» ya seleccionado, y
  // guardar sin mirar dejaba la caja apuntando a una impresora que no existe.
  // Se ve solo en el navegador, no leyendo el código.
  const cambiarModo = (modo: string) => {
    const aparato = (m: string) =>
      m === 'escpos' ? 'puerto' : m === 'windows' || m === 'documento' ? 'impresora' : null

    setO((x) => ({
      ...x,
      modo,
      puerto: aparato(modo) === aparato(x.modo) ? x.puerto : '',
    }))

    setGuardado(false)
  }

  return (
    <section className="carta p-4">
      <Encabezado
        icono={<Printer className="h-4 w-4 text-verde-oscuro" />}
        titulo="Impresora y cajón"
        estado={
          o.modo === 'ninguna'
            ? 'No imprime'
            : estado.impresoraDisponible
              ? 'Lista'
              : 'Configurada, sin responder'
        }
        bien={o.modo === 'ninguna' ? null : estado.impresoraDisponible}
      />

      <div className="mt-3 space-y-3">
        <LookupSelect
          etiqueta="Cómo imprime el ticket"
          items={MODOS_IMPRESORA}
          valor={MODOS_IMPRESORA.find((m) => m.code === o.modo)?.id ?? null}
          onCambio={(_, item) => cambiarModo(item?.code ?? 'ninguna')}
        />

        {porWindows && (
          <SelectorDeAparato
            etiqueta="Cuál de las impresoras"
            ayuda="Es la misma lista de Dispositivos e impresoras de Windows."
            vacio={
              dispositivos.data && !dispositivos.data.esWindows
                ? 'Este modo solo funciona en Windows.'
                : 'Windows no reporta ninguna impresora instalada.'
            }
            valor={o.puerto}
            onCambio={(v) => campo('puerto', v)}
            opciones={dispositivos.data?.impresoras ?? []}
            cargando={dispositivos.isFetching}
            onBuscar={() => void dispositivos.refetch()}
          />
        )}

        {/* Es la trampa del módulo y por eso está dicha en la pantalla: una
            impresora que no es térmica ACEPTA el trabajo sin protestar y
            después imprime una hoja en blanco, así que el que probó con «Print
            to PDF» y vio el PDF vacío no tiene ninguna pista de qué pasó. */}
        {porDocumento && (
          <p className="rounded-lg bg-crema-alt px-3 py-2 text-[12.5px] leading-relaxed text-tinta-suave">
            El ticket sale como una hoja de texto, así que sirve con cualquier impresora: de tinta,
            láser, o «Microsoft Print to PDF» para probar sin papel. Lo que este modo{' '}
            <strong>no</strong> hace es cortar el papel ni abrir el cajón — esas dos son órdenes de
            una térmica. Si la del mostrador es una comandera, elegí{' '}
            <strong>Térmica instalada en Windows</strong>: con este modo el ticket sale con la
            fuente de Windows en una hoja entera.
          </p>
        )}

        {porSerie && (
          <SelectorDeAparato
            etiqueta="Puerto"
            ayuda="En Windows es COM1, COM2… En Linux, /dev/usb/lp0."
            vacio="No hay ningún puerto serie disponible. Escribilo si sabés cuál es."
            valor={o.puerto}
            onCambio={(v) => campo('puerto', v)}
            opciones={dispositivos.data?.puertos ?? []}
            cargando={dispositivos.isFetching}
            onBuscar={() => void dispositivos.refetch()}
          />
        )}

        {o.modo !== 'ninguna' && (
          <label className="block">
            <span className="etiqueta">Ancho del papel</span>

            <div className="flex items-center gap-2">
              <input
                className="campo num max-w-[110px] text-right"
                inputMode="numeric"
                value={String(o.anchoCaracteres)}
                onChange={(e) => campo('anchoCaracteres', Number(e.target.value.replace(/\D/g, '')) || 0)}
              />

              <span className="text-[13px] text-tinta-tenue">caracteres</span>
            </div>

            <span className="mt-1 block text-[12.5px] text-tinta-tenue">
              32 si el papel es de 58 mm, 48 si es de 80 mm. Es el ancho del rollo, no el del
              equipo.
            </span>
          </label>
        )}

        {haceTicket && (
          <>
            <BotonAvanzado abierto={avanzado} onCambio={setAvanzado} />

            {avanzado && (
              <div className="grid gap-3 rounded-lg bg-crema-alt p-3 sm:grid-cols-2">
                {/* Los baudios son del CABLE: por la cola de Windows no hay
                    velocidad de puerto que elegir, y mostrar el campo ahí
                    haría creer que algo depende de él. */}
                {porSerie && (
                  <Numero etiqueta="Baudios" valor={o.baudios} onCambio={(v) => campo('baudios', v)} />
                )}

                <Numero
                  etiqueta="Líneas antes del corte"
                  valor={o.lineasDeCorte}
                  onCambio={(v) => campo('lineasDeCorte', v)}
                />

                <label className="block sm:col-span-2">
                  <span className="etiqueta">Comando para abrir el cajón</span>

                  <input
                    className="campo font-mono text-[13px]"
                    value={o.comandoCajon}
                    onChange={(e) => campo('comandoCajon', e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />

                  <span className="mt-1 block text-[12.5px] leading-relaxed text-tinta-tenue">
                    Bytes en decimal, separados por coma. El que viene sirve en casi todas; hay
                    modelos que usan el pin 2 y necesitan <code>27,112,1,25,250</code>.
                  </span>
                </label>
              </div>
            )}
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => guardar.mutate()}
            disabled={guardar.isPending}
            className="btn-primario"
          >
            {guardar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Guardar
          </button>

          {o.modo !== 'ninguna' && (
            <button
              type="button"
              onClick={() => probar.mutate()}
              disabled={probar.isPending}
              className="btn-secundario"
            >
              {probar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Imprimir una prueba
            </button>
          )}

          {/* El cajón se muestra siempre, incluso sin el permiso: es una de las
              acciones que un encargado puede autorizar con su PIN, y esconder
              el botón deja al cajero sin forma de pedirlo (ver el gotcha de la
              elevación). */}
          {o.modo !== 'ninguna' && !porDocumento && (
            <button
              type="button"
              onClick={() => cajon.mutate()}
              disabled={cajon.isPending}
              className="btn-secundario"
              title={puede('caja.abrir_cajon') ? undefined : 'Hace falta el PIN de un encargado'}
            >
              {cajon.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />}
              Abrir el cajón
            </button>
          )}

          {guardado && <span className="text-[13px] text-verde-oscuro">Guardado.</span>}
        </div>
      </div>
    </section>
  )
}

// ── Piezas ──────────────────────────────────────────────────────────────────

function Encabezado({
  icono,
  titulo,
  estado,
  bien,
}: {
  icono: React.ReactNode
  titulo: string
  estado: string
  bien: boolean | null
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-tinta">
        {icono}
        {titulo}
      </h2>

      <span
        className={
          bien === null
            ? 'text-[12.5px] text-tinta-tenue'
            : bien
              ? 'text-[12.5px] text-verde-oscuro'
              : 'text-[12.5px] text-ambar'
        }
      >
        {estado}
      </span>
    </div>
  )
}

function BotonAvanzado({
  abierto,
  onCambio,
}: {
  abierto: boolean
  onCambio: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onCambio(!abierto)}
      className="text-[13px] font-medium text-verde-oscuro hover:underline"
    >
      {abierto ? 'Ocultar lo avanzado' : 'Mostrar lo avanzado'}
    </button>
  )
}

function Texto({
  etiqueta,
  valor,
  onCambio,
}: {
  etiqueta: string
  valor: string
  onCambio: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="etiqueta">{etiqueta}</span>
      <input
        className="campo"
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
    </label>
  )
}

function Numero({
  etiqueta,
  valor,
  onCambio,
}: {
  etiqueta: string
  valor: number
  onCambio: (v: number) => void
}) {
  return (
    <label className="block">
      <span className="etiqueta">{etiqueta}</span>
      <input
        className="campo num text-right"
        inputMode="numeric"
        value={String(valor)}
        onChange={(e) => onCambio(Number(e.target.value.replace(/\D/g, '')) || 0)}
      />
    </label>
  )
}

// ── Tipos ───────────────────────────────────────────────────────────────────

interface OpcionesBalanza {
  modo: string
  puerto: string
  baudios: number
  bitsDeDatos: number
  paridad: string
  bitsDeParada: string
  patronTrama: string
  marcaEstable: string
  vencimientoMs: number
}

interface OpcionesImpresora {
  modo: string
  puerto: string
  baudios: number
  anchoCaracteres: number
  lineasDeCorte: number
  comandoCajon: string
}

interface EstadoEquipo {
  balanzaModo: string
  balanzaConectada: boolean
  impresoraModo: string
  impresoraDisponible: boolean
}

/**
 * El PROGRAMA de la caja: si hay una versión más nueva que la que corre acá.
 *
 * Es la otra mitad del OTA, y la que no existía. La pantalla se actualiza sola
 * desde hace rato; el ejecutable llegaba al mostrador cuando alguien se
 * acordaba de mandarlo por mensaje, porque vivía en un artefacto de GitHub de
 * un repositorio privado.
 *
 * Lo que este bloque hace y lo que NO, y está dicho en la pantalla porque es
 * lo que se malinterpreta: baja el archivo y lo verifica, pero <b>no instala</b>.
 * Reemplazar el ejecutable que está corriendo no lo puede hacer este proceso
 * —Windows tiene el archivo tomado—, así que el último paso es de una persona.
 *
 * Cuál es ese último paso depende de lo que se haya bajado, y por eso el texto
 * cambia: con el INSTALADOR es abrirlo y esperar —él cierra el programa,
 * reemplaza lo que hay y deja los datos donde están—, y con el ejecutable
 * suelto —que es lo que baja contra una nube que todavía no publicó ninguna
 * versión con instalador— hay que cerrar la caja y pisar el archivo a mano.
 * Decir siempre lo segundo mandaría a hacer a mano lo que el archivo ya hace.
 */
function ProgramaDeLaMaquina({
  programa,
}: {
  programa: NonNullable<Actualizacion['programa']>
}) {
  const [bajado, setBajado] = useState<{ ruta: string; version: string } | null>(null)

  const bajar = useMutation({
    mutationFn: () =>
      api.post<{ version: string; ruta: string; carpeta: string }>(
        '/actualizaciones/programa/bajar',
        {},
      ),
    onSuccess: (r) => setBajado({ ruta: r.ruta, version: r.version }),
    onError: (e) =>
      void avisar({
        titulo: 'No se pudo bajar el programa',
        detalle: mensajeDeError(e, 'No se pudo bajar el instalador.'),
      }),
  })

  return (
    <div className="mt-4 border-t border-borde pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[13.5px] font-semibold text-tinta">Programa de la caja</h3>

        <span
          className={clsx(
            'text-[12.5px]',
            programa.hayNueva ? 'text-ambar' : 'text-tinta-tenue',
          )}
        >
          {programa.hayNueva ? 'Hay una versión nueva' : 'Al día'}
        </span>
      </div>

      <dl className="mt-2 grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-[auto_1fr]">
        <dt className="text-tinta-tenue">La que corre en esta máquina</dt>
        <dd className="num text-tinta-suave">{programa.versionActual}</dd>

        {programa.hayNueva && (
          <>
            <dt className="text-tinta-tenue">La que hay publicada</dt>
            <dd className="num text-tinta-suave">
              {programa.version}
              {programa.tamanoBytes > 0 && (
                <span className="ml-2 text-tinta-tenue">({tamanoArchivo(programa.tamanoBytes)})</span>
              )}
            </dd>
          </>
        )}
      </dl>

      {programa.hayNueva && programa.notas && (
        <p className="mt-2 text-[13px] leading-relaxed text-tinta-suave">{programa.notas}</p>
      )}

      {programa.hayNueva && !bajado && (
        <>
          <p className="mt-2 text-[13px] leading-relaxed text-tinta-tenue">
            {programa.esInstalador ? (
              <>
                Esta parte no se actualiza sola: se baja el instalador y después hay que{' '}
                <strong>abrirlo</strong>. Él se encarga del resto. Los datos, la configuración y
                lo que esté sin subir quedan como están.
              </>
            ) : (
              <>
                Esta parte no se actualiza sola: se baja el archivo y después hay que{' '}
                <strong>cerrar el programa</strong>, reemplazarlo y volver a abrirlo. Los datos,
                la configuración y lo que esté sin subir quedan como están.
              </>
            )}
          </p>

          <button
            type="button"
            onClick={() => bajar.mutate()}
            disabled={bajar.isPending}
            className="btn-secundario mt-3"
          >
            {bajar.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {programa.esInstalador ? 'Bajar el instalador' : 'Bajar el programa nuevo'}
          </button>
        </>
      )}

      {bajado && (
        <div className="mt-3 rounded-lg bg-verde-tenue px-3 py-2.5 text-[13px] leading-relaxed text-verde-oscuro">
          <p>
            Listo: la versión <strong>{bajado.version}</strong> quedó en esta computadora.
          </p>

          {/* La RUTA completa, y seleccionable: sin ella hay que salir a
              buscar el archivo por toda la máquina, que es justo el paso en
              el que alguien se rinde. */}
          <p className="num mt-1 break-all select-all text-[12.5px]">{bajado.ruta}</p>

          <p className="mt-2">
            {programa.esInstalador
              ? 'Abrilo con doble clic y seguí los pasos: reemplaza el programa y deja los datos como están.'
              : 'Cerrá el programa, reemplazá el ejecutable viejo por ese y volvé a abrirlo.'}
          </p>
        </div>
      )}
    </div>
  )
}

interface Actualizacion {
  versionActual: string | null
  hayNueva: boolean
  version: string | null
  notas: string | null
  requiereActualizarPrograma: boolean

  // El PROGRAMA, que es otra cosa que la pantalla: la pantalla se actualiza
  // sola y el ejecutable hay que reemplazarlo a mano. Va opcional porque la
  // pantalla se actualiza por OTA y puede correr contra un host viejo que
  // todavía no manda el campo.
  programa?: {
    versionActual: string
    hayNueva: boolean
    version: string | null
    notas: string | null
    tamanoBytes: number

    /**
     * Si lo que se va a bajar es el INSTALADOR o el ejecutable pelado. Lo dice
     * el host y no lo deduce la pantalla, que es la regla ya escrita para la
     * URL de una imagen: el que sabe qué archivo baja es el que lo baja.
     *
     * Opcional porque el bundle se actualiza por OTA y puede correr contra un
     * ejecutable viejo que todavía no manda el campo; ausente se lee como el
     * comportamiento de antes, que es reemplazar el archivo a mano.
     */
    esInstalador?: boolean
  }
}

interface Registro {
  registrada: boolean
  nubeUrl: string
  nubeDeFabrica: string
  local?: string | null
  puntoDeVenta?: string | null
  prefijo?: string | null
}

interface Equipo {
  balanza: OpcionesBalanza
  impresora: OpcionesImpresora
  estado: EstadoEquipo
}
